/**
 * ThemeToggle - Light/dark mode switch
 */

import { Moon, Sun } from 'lucide-react';
import * as React from 'react';

import { useTheme } from '@/context/ThemeContext';
import { cn } from '@/lib/utils';

export interface ThemeToggleProps {
  className?: string;
}

export function ThemeToggle({ className }: ThemeToggleProps): React.ReactElement {
  const { resolvedTheme, toggleTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  return (
    <button
      type="button"
      onClick={toggleTheme}
      className={cn(
        'relative flex h-9 w-9 items-center justify-center rounded-pill border border-border-subtle bg-surface text-secondary',
        'transition-all duration-base ease-standard hover:bg-surface-subtle hover:text-primary',
        'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand/16',
        className
      )}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      <Sun
        className={cn(
          'absolute h-4 w-4 transition-all duration-slow ease-standard',
          isDark ? 'rotate-90 scale-0 opacity-0' : 'rotate-0 scale-100 opacity-100'
        )}
        aria-hidden="true"
      />
      <Moon
        className={cn(
          'absolute h-4 w-4 transition-all duration-slow ease-standard',
          isDark ? 'rotate-0 scale-100 opacity-100' : '-rotate-90 scale-0 opacity-0'
        )}
        aria-hidden="true"
      />
    </button>
  );
}
